import React, { useContext, useState } from "react";
import { Box, Chip } from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import { useCountdown, getTimeParts } from "src/utils/useCountdown.js";
import { customEvent } from "src/utils/gtag.js";
import OfferModal, { OFFER_DISCOUNT_PERCENT } from "./OfferModal.js";

/**
 * Compact human time for the nav pill: "2d 11h" beyond 24 hours, "5h 32m"
 * between 1 and 24 hours, "MM:SS" under an hour. A pill is a reminder, not a
 * ticking countdown, so it never shows raw uncapped hours.
 *
 * @param {number} totalMs
 * @returns {string}
 */
export const formatPillCountdown = (totalMs) => {
  const { hours, minutes, seconds } = getTimeParts(totalMs);
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  if (hours >= 1) {
    return `${hours}h ${minutes}m`;
  }
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

/**
 * Reads the limited-time-offer state off the auth `user` object. Active while
 * `offerDeadlineAt` is in the future; older sessions may lack the field.
 *
 * @param {object|null|undefined} user
 * @returns {{ active: boolean, deadlineAt: (string|Date|null) }}
 */
export const getOfferState = (user) => {
  const deadlineAt = user?.offerDeadlineAt ?? null;
  const active =
    deadlineAt != null && new Date(deadlineAt).getTime() > Date.now();
  return { active, deadlineAt };
};

/**
 * Reads remaining trial days off the auth `user` object, preferring a live
 * recompute from `trialEndsAt` over the `trialDaysLeft` session snapshot.
 *
 * @param {object|null|undefined} user
 * @returns {number|null}
 */
export const getTrialDaysLeft = (user) => {
  if (!user) return null;
  if (user.trialEndsAt != null) {
    const ms = new Date(user.trialEndsAt).getTime() - Date.now();
    if (Number.isFinite(ms)) {
      return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
    }
  }
  if (typeof user.trialDaysLeft === "number") {
    return Math.max(0, user.trialDaysLeft);
  }
  return null;
};

/**
 * Whether the user has already paid. A cardless free trial sets no
 * subscription record, so trialing users stay in the pill's audience until
 * they convert.
 *
 * @param {object|null|undefined} user
 * @param {number|null} trialDaysLeft
 * @returns {boolean}
 */
export const isPayingMember = (user, trialDaysLeft) => {
  if (!user?.hasValidSubscription) return false;
  const trialingWithoutSubscription =
    trialDaysLeft != null &&
    trialDaysLeft > 0 &&
    !user.consumerSubscription &&
    !user.lifetimeMember;
  return !trialingWithoutSubscription;
};

/**
 * Derives the pill's display state from the auth `user` object.
 *
 * @param {object|null|undefined} user
 * @returns {{ offerActive: boolean, deadlineAt: (string|Date|null), trialDaysLeft: (number|null), trialActive: boolean, paying: boolean }}
 */
export const getOfferPillState = (user) => {
  const { active: offerActive, deadlineAt } = getOfferState(user);
  const trialDaysLeft = getTrialDaysLeft(user);
  const trialActive = trialDaysLeft != null && trialDaysLeft > 0;
  const paying = isPayingMember(user, trialDaysLeft);
  return { offerActive, deadlineAt, trialDaysLeft, trialActive, paying };
};

/**
 * Whether the pill renders for this user — lets the topbar swap the "Go Plus"
 * button for the pill without duplicating the visibility logic.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export const shouldShowOfferPill = (user) => {
  const { offerActive, trialActive, paying } = getOfferPillState(user);
  return !paying && (offerActive || trialActive);
};

/**
 * Persistent nav pill: live offer countdown while an offer is active,
 * otherwise the "Trial ends in N days" state. Offer mode opens the offer
 * modal; trial mode navigates to checkout.
 */
export const OfferPill = () => {
  const { user } = useContext(AuthContext) ?? {};
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const { offerActive, deadlineAt, trialDaysLeft, trialActive, paying } =
    getOfferPillState(user);

  // With no offer to tick toward, count down to the trial end instead so the
  // label still flips at the trial boundary in long-lived tabs.
  const countdownTarget = offerActive
    ? deadlineAt
    : trialActive
      ? user?.trialEndsAt
      : null;
  const { totalMs, expired } = useCountdown(countdownTarget);

  const showOffer = offerActive && !expired;
  const showPill = !paying && (showOffer || trialActive);

  // Keep an already-open modal mounted through countdown expiry.
  if (!showPill && !modalOpen) return null;

  const label = showOffer
    ? `${OFFER_DISCOUNT_PERCENT}% off · ${formatPillCountdown(totalMs)}`
    : `Trial ends in ${trialDaysLeft} ${trialDaysLeft === 1 ? "day" : "days"}`;

  const handleClick = () => {
    customEvent({
      name: "offer_pill_clicked",
      category: "Offer",
      label: "Offer Pill Clicked",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        offer_active: showOffer,
      },
    });
    if (showOffer) {
      setModalOpen(true);
    } else {
      navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
        state: { source: "offer_pill" },
      });
    }
  };

  return (
    <>
      {showPill && (
        <Box sx={{ display: "inline-flex", alignItems: "center" }}>
          <Chip
            data-testid="offer-pill"
            icon={<LocalOfferOutlinedIcon />}
            label={label}
            onClick={handleClick}
            color={showOffer ? "primary" : "default"}
            variant={showOffer ? "filled" : "outlined"}
            sx={{
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              cursor: "pointer",
              mr: { xs: 1, sm: 2 },
            }}
          />
        </Box>
      )}
      <OfferModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        deadline={deadlineAt}
      />
    </>
  );
};

export default OfferPill;
